import { useEffect, useState } from "react";
import UserDetails from "./details";
import UserForm from "./form";
import { FormProvider, useForm } from "react-hook-form";
import { schema, type NewUserFormData } from "@/schemas/users/new-user";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router";
import { useHeaderButton } from "@/contexts/header-button-context";
import { Button, Icon, useToast } from "@elaniintc/goes-design-system";
import { useCreateUser } from "@/queries/users/users.api";
import { cn } from "@/utils/theme";
import type { AxiosError } from "axios";

const NewUser = () => {
  const methods = useForm<NewUserFormData>({
    mode: "onChange",
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      last_name: "",
      email: "",
      phone_number: "",
      assigned_institution_id: "",
      department_id: "",
      registered_position_id: "",
      dui: "",
      role_id: "",
    },
  });

  const [selectedRole, setSelectedRole] = useState<{
    label: string;
    type: string;
  } | null>(null);
  const [selectedUnit, setSelectedUnit] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [selectedOrganization, setSelectedOrganization] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [selectedPosition, setSelectedPosition] = useState<{
    label: string;
    value: string;
  } | null>(null);

  const navigate = useNavigate();
  const { setComp } = useHeaderButton();

  const { mutateAsync, isPending } = useCreateUser();
  const { showToast } = useToast();

  const onSubmit = async (data: NewUserFormData) => {
    try {
      await mutateAsync(data);
      showToast({
        title: "Usuario creado exitosamente",
        description: "El usuario ha sido creado correctamente.",
        options: {
          type: "success",
          duration: 4000,
        },
      });
      navigate("/usuarios");
    } catch (e) {
      const error = e as AxiosError<{
        message: string;
        errors?: {
          [key: string]: string[];
        };
      }>;
      if (error.response?.status === 422) {
        Object.keys(error.response.data.errors || {}).forEach((key) => {
          if (key === "email") {
            methods.setError("email", {
              type: "manual",
              message: error?.response?.data?.errors?.email[0] || "",
            });
          }
          if (key === "dui") {
            methods.setError("dui", {
              type: "manual",
              message: error?.response?.data?.errors?.dui[0] || "",
            });
          }
        });
      }
      showToast({
        title: "Error al crear el usuario",
        description: "Revisa los datos ingresados e inténtalo de nuevo.",
        options: {
          type: "error",
          duration: 4000,
        },
      });
    }
  };

  useEffect(() => {
    setComp(
      <>
        <Button
          variant="quiet"
          size="xs"
          type="button"
          onClick={() => navigate(-1)}
        >
          Cancelar
          <Icon type="far" name="times" />
        </Button>
        <Button
          variant="default"
          size="xs"
          form="new-user-form"
          type="submit"
          disabled={!methods.formState.isValid || isPending}
        >
          Crear usuario
          <Icon
            type="far"
            name={isPending ? "spinner-third" : "plus"}
            className={cn("text-current", isPending && "animate-spin")}
          />
        </Button>
      </>,
    );
    return () => {
      setComp(null);
    };
  }, [navigate, setComp, isPending, methods.formState.isValid]);

  return (
    <div className="grid grid-cols-1 tablet:grid-cols-2 gap-3">
      <FormProvider {...methods}>
        <UserDetails
          selectedPosition={selectedPosition}
          selectedOrganization={selectedOrganization}
          selectedUnit={selectedUnit}
          selectedRole={selectedRole}
        />
        <UserForm
          setSelectedRole={setSelectedRole}
          setSelectedUnit={setSelectedUnit}
          setSelectedOrganization={setSelectedOrganization}
          setSelectedPosition={setSelectedPosition}
          onSubmit={onSubmit}
        />
      </FormProvider>
    </div>
  );
};

export default NewUser;
